import PropTypes from 'prop-types';
import React from 'react';
import { BadgeContainer, BadgeCount } from './StyledComponents';

/**
 * Unified Badge Component
 *
 * A flexible badge component that consolidates all badge variations used throughout the app.
 * Supports different variants, shapes, sizes, optional count badges, and icons.
 *
 * @example
 * // Basic badge
 * <Badge variant="success">Active</Badge>
 *
 * // Pill-shaped badge
 * <Badge shape="pill">Tag</Badge>
 *
 * // Badge with count
 * <Badge shape="pill" count={5}>Category</Badge>
 *
 * // Badge with icon
 * <Badge variant="accent" icon={<svg>...</svg>}>Scanned</Badge>
 *
 * // Outline badge
 * <Badge outline>Featured</Badge>
 *
 * // Custom color badge
 * <Badge color="#8B5CF6">Premium</Badge>
 */

const Badge = ({
  children,
  variant = 'default',
  shape = 'rounded',
  size = 'medium',
  color,
  count,
  icon,
  outline = false,
  uppercase = false,
  interactive = false,
  className,
  onClick,
  ...props
}) => {
  return (
    <BadgeContainer
      variant={variant}
      shape={shape}
      size={size}
      color={color}
      outline={outline}
      uppercase={uppercase}
      interactive={interactive}
      className={className}
      onClick={onClick}
      hasIcon={!!icon}
      {...props}
    >
      {icon && <span className="badge-icon">{icon}</span>}
      <span className="badge-text">{children}</span>
      {count !== undefined && count !== null && (
        <BadgeCount size={size}>{count}</BadgeCount>
      )}
    </BadgeContainer>
  );
};

Badge.propTypes = {
  children: PropTypes.node.isRequired,
  /** Badge color variant */
  variant: PropTypes.oneOf([
    'default',
    'success',
    'primary',
    'info',
    'warning',
    'error',
    'alert',
    'premium',
    'new',
    'accent',
    'neutral',
    'location',
    'dark',
    'occasional',
  ]),
  /** Badge shape */
  shape: PropTypes.oneOf(['rounded', 'pill', 'square']),
  /** Badge size */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Custom color (hex or rgb) */
  color: PropTypes.string,
  /** Optional count badge */
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Optional icon element */
  icon: PropTypes.node,
  /** Outline style */
  outline: PropTypes.bool,
  /** Uppercase text */
  uppercase: PropTypes.bool,
  /** Interactive (shows hover effects) */
  interactive: PropTypes.bool,
  /** Additional className */
  className: PropTypes.string,
  /** Click handler */
  onClick: PropTypes.func,
};

export default Badge;
